// CategoryFilter.jsx - Updated with multi-grade support
// Replace at: src/components/leitner/CategoryFilter.jsx

import React, { useMemo } from 'react';
import { Filter, X } from 'lucide-react';

const CategoryFilter = ({ categories, selectedFilters, onFilterChange, onClear, questions }) => {
  // Filter topics based on selected subject and grades
  const availableTopics = useMemo(() => {
    if (!questions || questions.length === 0) return categories.topics;

    const selectedGrades = selectedFilters.grades || (selectedFilters.grade ? [selectedFilters.grade] : []);

    const filteredQuestions = questions.filter(q => {
      let matches = true;
      if (selectedFilters.subject && q.subject !== selectedFilters.subject) {
        matches = false;
      }
      
      // Handle multi-grade filtering
      if (selectedGrades.length > 0) {
        const questionGrades = Array.isArray(q.grades) ? q.grades : (q.grade ? [q.grade] : []);
        const hasMatchingGrade = selectedGrades.some(g => questionGrades.includes(g));
        if (!hasMatchingGrade) {
          matches = false;
        }
      }
      
      return matches;
    });

    const topicsSet = new Set();
    filteredQuestions.forEach(q => {
      if (q.topics && Array.isArray(q.topics)) {
        q.topics.forEach(topic => topicsSet.add(topic));
      }
      if (q.type === 'stem' && q.subQuestions) {
        q.subQuestions.forEach(subQ => {
          if (subQ.topics && Array.isArray(subQ.topics)) {
            subQ.topics.forEach(topic => topicsSet.add(topic));
          }
        });
      }
    });

    return Array.from(topicsSet).sort();
  }, [questions, selectedFilters.subject, selectedFilters.grades, selectedFilters.grade, categories.topics]);

  const handleSubjectChange = (subject) => {
    onFilterChange({
      subject: subject || null,
      grades: selectedFilters.grades || [],
      topics: []
    });
  };

  // Multi-grade toggle
  const handleGradeToggle = (grade) => {
    const currentGrades = selectedFilters.grades || [];
    const newGrades = currentGrades.includes(grade)
      ? currentGrades.filter(g => g !== grade)
      : [...currentGrades, grade];
    
    onFilterChange({
      subject: selectedFilters.subject,
      grades: newGrades,
      topics: []
    });
  };

  const handleTopicToggle = (topic) => {
    const currentTopics = selectedFilters.topics || [];
    const newTopics = currentTopics.includes(topic)
      ? currentTopics.filter(t => t !== topic)
      : [...currentTopics, topic];
    
    onFilterChange({
      ...selectedFilters,
      topics: newTopics
    });
  };

  const selectedGrades = selectedFilters.grades || [];
  const hasActiveFilters = selectedFilters.subject || selectedGrades.length > 0 || 
    (selectedFilters.topics && selectedFilters.topics.length > 0);

  return (
    <div className="bg-slate-800 p-4 rounded-lg border border-slate-700 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-indigo-400" />
          <h3 className="text-lg font-semibold text-slate-200">Filter Questions</h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 px-3 py-1 text-sm text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-md transition"
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Subject Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Subject
          </label>
          <select
            value={selectedFilters.subject || ''}
            onChange={(e) => handleSubjectChange(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Subjects</option>
            {categories.subjects?.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
        </div>

        {/* Grade Filter - Multi-select buttons */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Grades (click to toggle)
          </label>
          <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto bg-slate-700 border border-slate-600 rounded-md p-2">
            {categories.grades?.length === 0 ? (
              <p className="text-sm text-slate-400">No grades available</p>
            ) : (
              categories.grades?.map(grade => (
                <button
                  key={grade}
                  type="button"
                  onClick={() => handleGradeToggle(grade)}
                  className={`px-2 py-1 text-xs rounded transition ${
                    selectedGrades.includes(grade)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  }`}
                >
                  {grade}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Topics Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Topics
            {(selectedFilters.subject || selectedGrades.length > 0) && (
              <span className="text-xs text-slate-400 ml-2">
                (filtered by {[selectedFilters.subject, selectedGrades.length > 0 ? `${selectedGrades.length} grade(s)` : null].filter(Boolean).join(' & ')})
              </span>
            )}
          </label>
          <div className="max-h-32 overflow-y-auto bg-slate-700 border border-slate-600 rounded-md p-2">
            {availableTopics.length === 0 ? (
              <p className="text-sm text-slate-400">
                {selectedFilters.subject || selectedGrades.length > 0
                  ? 'No topics for selected filters' 
                  : 'No topics available'}
              </p>
            ) : (
              availableTopics.map(topic => (
                <label key={topic} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-600 px-2 rounded">
                  <input
                    type="checkbox"
                    checked={(selectedFilters.topics || []).includes(topic)}
                    onChange={() => handleTopicToggle(topic)}
                    className="rounded bg-slate-600 border-slate-500 text-indigo-500 focus:ring-indigo-600"
                  />
                  <span className="text-sm text-slate-200">{topic}</span>
                </label>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className="flex flex-wrap gap-2">
            {selectedFilters.subject && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                Subject: {selectedFilters.subject}
                <button
                  onClick={() => handleSubjectChange(null)}
                  className="hover:bg-indigo-700 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedGrades.map(grade => (
              <span key={grade} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                Grade: {grade}
                <button
                  onClick={() => handleGradeToggle(grade)}
                  className="hover:bg-indigo-700 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {selectedFilters.topics?.map(topic => (
              <span key={topic} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                {topic}
                <button
                  onClick={() => handleTopicToggle(topic)}
                  className="hover:bg-indigo-700 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryFilter;